import { queryClient } from '~app/query-client';

import { getUserRolesQueryOptions } from '~entities/users/api/get-user-roles';
import { getCurrentUser } from '~entities/users/model/user-store';
import { UserRoleLabels, type UserRoles } from '~entities/users/model/types';

import { normalizeRoleName } from './normalize-role-name';

export { getRoleValue } from './get-role-value';

export const hasRoleAccess = (
  requiredRoleName: UserRoles,
  minRoleValue?: number,
): boolean => {
  const currentUser = getCurrentUser();

  if (!currentUser?.role) {
    return false;
  }

  const userRoleName = normalizeRoleName(currentUser.role.name);
  const requiredRoleNameLower = normalizeRoleName(requiredRoleName);

  if (userRoleName === requiredRoleNameLower) {
    return true;
  }

  // Check by minimum role value (higher value = higher privilege)
  if (minRoleValue !== undefined) {
    const userRoleValue = currentUser.role.value;
    if (userRoleValue >= minRoleValue) {
      return true;
    }
  }

  return false;
};

export const checkMinRoleValue = async (
  requiredRoleName: UserRoles,
): Promise<boolean> => {
  const currentUser = getCurrentUser();

  if (!currentUser?.role) {
    return false;
  }

  try {
    const roleLabel = UserRoleLabels[requiredRoleName];
    const userRoles = await queryClient.ensureQueryData(
      getUserRolesQueryOptions(),
    );

    if (!Array.isArray(userRoles)) {
      return false;
    }

    const requiredRole = userRoles.find(
      (role) => normalizeRoleName(role.name) === normalizeRoleName(roleLabel),
    );

    if (!requiredRole) {
      return false;
    }

    const userRoleValue = currentUser.role.value;
    const requiredRoleValue = requiredRole.value;

    // Access granted if user's role value is at least equal to required role value
    return userRoleValue >= requiredRoleValue;
  } catch {
    return false;
  }
};
